import { Link } from "react-router-dom";
import { Compass, Briefcase, MapPinned, FileText, Wallet, ClipboardCheck, Bot, Hotel, Plane, ArrowRight, ChevronLeft, ChevronRight, Shield, Smartphone, Share, PlusSquare, Menu as MenuIcon, Download as DownloadIcon } from "lucide-react";
import PersonalizeWidget from "../components/msp/PersonalizeWidget";
import HealthVaultStatus from "../components/msp/HealthVaultStatus";
import ShareBoard from "../components/ShareBoard";
import AppShellMockup from "../components/msp/AppShellMockup";
import PulseNetworkFeed from "../components/network/PulseNetworkFeed";
import InstallQrCard from "../components/InstallQrCard";

/* /app — Native-shell landing (iter218). Left side keeps the marketing hero;
   right side is now a fully interactive phone mockup — every icon is a real
   <Link> straight to its section, matching the mobile home screen users get
   after installing the PWA. */

const BOARDS = [
  { to: "/msp/trips",       label: "My Trips",         desc: "Upcoming, past & saved", Icon: Briefcase,     tint: "from-sky-100 to-sky-50",     ic: "text-sky-600" },
  { to: "/msp/plan",        label: "Plan a Trip",      desc: "Destinations & dates",   Icon: Compass,        tint: "from-emerald-100 to-emerald-50", ic: "text-emerald-600" },
  { to: "/msp/hotels",      label: "Hotels",           desc: "Search & compare stays", Icon: Hotel,          tint: "from-fuchsia-100 to-fuchsia-50", ic: "text-fuchsia-600" },
  { to: "/msp/flights",     label: "Flights",          desc: "Round-trip · One-way",   Icon: Plane,          tint: "from-blue-100 to-blue-50",   ic: "text-blue-600" },
  { to: "/msp/maps",        label: "Maps",             desc: "Attractions & transit",  Icon: MapPinned,      tint: "from-lime-100 to-lime-50",   ic: "text-lime-700" },
  { to: "/msp/documents",   label: "Documents",        desc: "Passports · Visas",      Icon: FileText,       tint: "from-amber-100 to-amber-50", ic: "text-amber-600" },
  { to: "/msp/expenses",    label: "Expenses",         desc: "Track & split costs",    Icon: Wallet,         tint: "from-rose-100 to-rose-50",   ic: "text-rose-600" },
  { to: "/msp/checklists",  label: "Checklists",       desc: "Pre · During · Post",    Icon: ClipboardCheck, tint: "from-teal-100 to-teal-50",   ic: "text-teal-600" },
  { to: "/msp/ai-assistant",label: "AI Travel Assistant", desc: "Ask anything travel", Icon: Bot,            tint: "from-violet-100 to-violet-50", ic: "text-violet-600" },
  { to: "/app/vault",       label: "My Health Vault",  desc: "Private · Locked · Safe",Icon: Shield,         tint: "from-emerald-100 to-emerald-50", ic: "text-emerald-600" },
];

const ONBOARD = [
  { title: "Explore the World",      body: "Top destinations, hidden gems, and local experiences.",         hue: "bg-sky-50" },
  { title: "Seamless Transport",     body: "Compare flights, trains, buses, ferries and car rentals.",      hue: "bg-emerald-50" },
  { title: "Plan & Organize Trips",  body: "Itineraries, bookings and expenses — all in one place.",        hue: "bg-amber-50" },
  { title: "Connect with Travelers", body: "Communities, tips and questions from a global crew.",           hue: "bg-fuchsia-50" },
  { title: "Travel Smarter Every Day", body: "Real-time alerts, deals and personal recommendations.",       hue: "bg-violet-50" },
];

export default function MSPLanding() {
  const qrData = typeof window !== "undefined" ? window.location.origin + "/install" : "https://travelerpulse.pro/install";
  return (
    <div className="bg-[#F4F8FE] min-h-screen overflow-x-hidden" data-testid="msp-landing">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-b from-white via-sky-50/50 to-transparent" />
        <div className="relative max-w-6xl mx-auto px-5 lg:px-8 pt-10 pb-14 grid lg:grid-cols-[1.15fr_1fr] gap-10 items-center">
          <div className="min-w-0">
            <div className="text-[10px] sm:text-[11px] font-mono uppercase tracking-[0.14em] sm:tracking-[0.28em] text-emerald-700 mb-4" data-testid="msp-eyebrow">
              My Social Pulse™ · Powered by TravelerPulse.pro
            </div>
            <h1 className="font-serif font-800 text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-blue-900" data-testid="msp-hero-title">
              Explore. <span className="text-amber-600">Plan.</span> <br />Share. Travel.
            </h1>
            <p className="mt-5 text-lg text-slate-700 max-w-xl leading-relaxed" data-testid="msp-hero-sub">
              Your complete travel life — organized in one warm, private place. Trips, documents, expenses, checklists and a personal AI assistant, right in your pocket.
            </p>

            <div className="mt-7 flex flex-wrap items-center gap-3">
              <a href="#install" data-testid="msp-cta-install"
                className="inline-flex items-center gap-2 rounded-2xl bg-emerald-600 text-white px-5 py-3 hover:brightness-110 transition font-700 shadow-sm">
                <Smartphone className="w-4 h-4" /> Save app to your phone
              </a>
              <Link to="/msp/plan" data-testid="msp-cta-open-preview"
                className="inline-flex items-center gap-2 rounded-2xl bg-blue-600 text-white px-5 py-3 hover:brightness-110 transition font-700">
                Open web preview
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>

          {/* Interactive app shell — every icon deep-links to its section */}
          <div className="relative w-full min-w-0 justify-self-stretch" data-testid="msp-hero-preview">
            <AppShellMockup />
            <p className="mt-4 text-center text-[11px] text-slate-500">
              Tap any icon — you're already in the app. Install to your home screen for one-tap access.
            </p>
          </div>
        </div>
      </section>

      {/* PERSONALIZED DASHBOARD STRIP */}
      <PersonalizeWidget />

      {/* HEALTH VAULT STATUS (Slice F integration) — non-sensitive summary tiles.
          Renders only for signed-in users; empty state prompts them to start the vault. */}
      <HealthVaultStatus />

      {/* Pulse Network cross-portal feed — auto-hides while the engine has 0 articles. */}
      <PulseNetworkFeed portalSlug="travelerpulse" limit={6} lang="en" trustLevel="all" />

      {/* FEATURE BOARDS */}
      <section className="max-w-6xl mx-auto px-5 lg:px-8 pt-8 pb-14">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="text-[11px] font-mono uppercase tracking-[0.28em] text-blue-700">Inside the app</div>
            <h2 className="font-serif font-800 text-3xl sm:text-4xl text-blue-900 tracking-tight mt-1">Everything for your travel life</h2>
          </div>
          <Link to="/msp/plan" data-testid="msp-primary-cta"
            className="hidden sm:inline-flex items-center gap-1.5 text-blue-700 font-700 hover:underline">
            Start planning <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4" data-testid="msp-boards">
          {BOARDS.map(({ to, label, desc, Icon, tint, ic }) => (
            <div key={label} className={`relative group rounded-3xl bg-gradient-to-br ${tint} border border-white shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all p-5 min-h-[128px] flex flex-col justify-between`}>
              <div className="absolute top-3 right-3 opacity-70 group-hover:opacity-100 transition-opacity">
                <ShareBoard url={to} title={`${label} · My Social Pulse™`} text={`${label} — ${desc}`}
                  testid={`msp-board-${label.toLowerCase().replace(/\s+/g,"-")}-share`} />
              </div>
              <Link to={to} data-testid={`msp-board-${label.toLowerCase().replace(/\s+/g,"-")}`} className="contents">
                <div className={`w-11 h-11 rounded-2xl bg-white ${ic} grid place-items-center shadow-sm`}>
                  <Icon className="w-5 h-5" strokeWidth={2} />
                </div>
                <div>
                  <div className="font-serif font-800 text-lg text-slate-900 leading-tight">{label}</div>
                  <div className="text-xs text-slate-600 mt-1">{desc}</div>
                </div>
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* ONBOARDING STRIP */}
      <section className="bg-white border-y border-slate-100 py-14 overflow-hidden" data-testid="msp-onboarding">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <div className="flex items-end justify-between mb-6">
            <h2 className="font-serif font-800 text-3xl text-blue-900 tracking-tight">Get started in 5 easy steps</h2>
            <div className="hidden sm:flex items-center gap-2 text-slate-500">
              <ChevronLeft className="w-4 h-4" /> Swipe <ChevronRight className="w-4 h-4" />
            </div>
          </div>
          <div className="flex overflow-x-auto snap-x snap-mandatory gap-4 pb-3 scrollbar-none" style={{ scrollbarWidth: "none" }}>
            {ONBOARD.map((o, idx) => (
              <div key={o.title} className="snap-start shrink-0 w-[260px] sm:w-[280px] rounded-3xl border border-slate-200 bg-white p-6"
                data-testid={`msp-onboard-${idx + 1}`}>
                <div className={`w-full h-32 rounded-2xl ${o.hue} grid place-items-center text-3xl font-serif font-800 text-blue-900`}>
                  {idx + 1}
                </div>
                <div className="mt-4 font-serif font-800 text-lg text-blue-900 leading-tight">{o.title}</div>
                <div className="mt-1.5 text-sm text-slate-600 leading-relaxed">{o.body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INSTALL — Save the web app to your phone */}
      <section id="install" className="max-w-6xl mx-auto px-5 lg:px-8 py-14 overflow-hidden" data-testid="msp-install">
        <div className="text-center mb-8">
          <div className="text-[11px] font-mono uppercase tracking-[0.28em] text-emerald-700 mb-2">Install · No app store needed</div>
          <h2 className="font-serif font-800 text-3xl sm:text-4xl text-blue-900">Save the app to your phone</h2>
          <p className="text-slate-600 mt-3 max-w-2xl mx-auto">TravelerPulse installs directly from your browser — no App Store, no download, no permissions. Add it to your home screen and it opens like a native app.</p>
        </div>

        {/* QR + share sheet */}
        <InstallQrCard variant="full" />

        {/* Step-by-step instructions */}
        <div className="mt-8 grid md:grid-cols-2 gap-5" data-testid="msp-install-steps">
          {/* iOS */}
          <div className="rounded-2xl border-2 border-slate-200 bg-white p-4 sm:p-6 min-w-0" data-testid="msp-install-ios">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-9 h-9 rounded-xl bg-slate-900 text-white grid place-items-center font-800"></div>
              <div>
                <div className="font-serif font-800 text-lg text-slate-900 leading-none">iPhone &amp; iPad</div>
                <div className="text-xs text-slate-500 mt-0.5">Safari · 15 seconds</div>
              </div>
            </div>
            <ol className="space-y-2.5 text-sm text-slate-700">
              <li className="flex gap-2.5"><span className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-800 grid place-items-center font-800 text-xs shrink-0">1</span><span className="min-w-0 flex-1">Open <b>travelerpulse.pro</b> in <b>Safari</b> (not Chrome — Chrome on iOS can't install).</span></li>
              <li className="flex gap-2.5"><span className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-800 grid place-items-center font-800 text-xs shrink-0">2</span><span className="min-w-0 flex-1">Tap the <b>Share</b> button <Share className="w-4 h-4 inline -mt-0.5 text-blue-600" /> at the bottom of the screen.</span></li>
              <li className="flex gap-2.5"><span className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-800 grid place-items-center font-800 text-xs shrink-0">3</span><span className="min-w-0 flex-1">Scroll down and tap <b>Add to Home Screen</b> <PlusSquare className="w-4 h-4 inline -mt-0.5 text-slate-600" />.</span></li>
              <li className="flex gap-2.5"><span className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-800 grid place-items-center font-800 text-xs shrink-0">4</span><span className="min-w-0 flex-1">Tap <b>Add</b> in the top-right — the TravelerPulse icon appears on your home screen.</span></li>
            </ol>
          </div>

          {/* Android */}
          <div className="rounded-2xl border-2 border-slate-200 bg-white p-4 sm:p-6 min-w-0" data-testid="msp-install-android">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-9 h-9 rounded-xl bg-emerald-600 text-white grid place-items-center"><DownloadIcon className="w-5 h-5" /></div>
              <div>
                <div className="font-serif font-800 text-lg text-slate-900 leading-none">Android</div>
                <div className="text-xs text-slate-500 mt-0.5">Chrome, Edge or Samsung Internet · 15 seconds</div>
              </div>
            </div>
            <ol className="space-y-2.5 text-sm text-slate-700">
              <li className="flex gap-2.5"><span className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-800 grid place-items-center font-800 text-xs shrink-0">1</span><span className="min-w-0 flex-1">Open <b>travelerpulse.pro</b> in <b>Chrome</b> (or your default browser).</span></li>
              <li className="flex gap-2.5"><span className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-800 grid place-items-center font-800 text-xs shrink-0">2</span><span className="min-w-0 flex-1">Tap the <b>⋮ menu</b> <MenuIcon className="w-4 h-4 inline -mt-0.5 text-slate-600" /> in the top-right corner.</span></li>
              <li className="flex gap-2.5"><span className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-800 grid place-items-center font-800 text-xs shrink-0">3</span><span className="min-w-0 flex-1">Tap <b>Install app</b> or <b>Add to Home screen</b>.</span></li>
              <li className="flex gap-2.5"><span className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-800 grid place-items-center font-800 text-xs shrink-0">4</span><span className="min-w-0 flex-1">Confirm <b>Install</b> — the app icon lands on your home screen instantly.</span></li>
            </ol>
          </div>
        </div>

        <div className="mt-6 text-center">
          <Link to="/install" data-testid="msp-install-full-guide"
            className="inline-flex items-center gap-1.5 text-emerald-700 font-700 hover:underline text-sm">
            Full install guide with screenshots <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
